import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Trash2, AlertTriangle } from 'lucide-react'
import type { TimetableEntry } from '@/services/api/timetable'

interface ValidityGroup {
  key: string
  validFrom: string | null
  validTo: string | null
  label: string
  entries: TimetableEntry[]
}

interface DeleteValidityDialogProps {
  isOpen: boolean
  selectedEntries: TimetableEntry[]
  onClose: () => void
  onConfirmDelete: (entryIdsToDelete: Set<string>) => void
  deleting: boolean
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatPeriodLabel(validFrom: string | null, validTo: string | null): string {
  if (!validFrom && !validTo) return 'Unbegrenzt gültig'
  if (!validFrom) return `Bis ${formatDate(validTo!)}`
  if (!validTo) return `Ab ${formatDate(validFrom)}`
  return `${formatDate(validFrom)} – ${formatDate(validTo)}`
}

function isPeriodCurrent(validFrom: string | null, validTo: string | null): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  if (validFrom) {
    const from = new Date(validFrom)
    if (today < from) return false
  }
  if (validTo) {
    const to = new Date(validTo)
    if (today > to) return false
  }
  return true
}

export function DeleteValidityDialog({
  isOpen,
  selectedEntries,
  onClose,
  onConfirmDelete,
  deleting,
}: DeleteValidityDialogProps) {
  const validityGroups = useMemo<ValidityGroup[]>(() => {
    const groupMap = new Map<string, ValidityGroup>()

    for (const entry of selectedEntries) {
      const vf = entry.valid_from || null
      const vt = entry.valid_to || null
      const key = `${vf || 'null'}_${vt || 'null'}`

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          validFrom: vf,
          validTo: vt,
          label: formatPeriodLabel(vf, vt),
          entries: [],
        })
      }
      groupMap.get(key)!.entries.push(entry)
    }

    const groups = Array.from(groupMap.values())
    groups.sort((a, b) => {
      const aCurrent = isPeriodCurrent(a.validFrom, a.validTo)
      const bCurrent = isPeriodCurrent(b.validFrom, b.validTo)
      if (aCurrent && !bCurrent) return -1
      if (!aCurrent && bCurrent) return 1
      const aFrom = a.validFrom || '0000-00-00'
      const bFrom = b.validFrom || '0000-00-00'
      return aFrom.localeCompare(bFrom)
    })

    return groups
  }, [selectedEntries])

  const [selectedPeriodKeys, setSelectedPeriodKeys] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (isOpen) {
      setSelectedPeriodKeys(new Set(validityGroups.map(g => g.key)))
    }
  }, [isOpen, validityGroups])

  const togglePeriod = (key: string) => {
    setSelectedPeriodKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedPeriodKeys(new Set(validityGroups.map(g => g.key)))
  }

  const deselectAll = () => {
    setSelectedPeriodKeys(new Set())
  }

  const allSelected = selectedPeriodKeys.size === validityGroups.length
  const noneSelected = selectedPeriodKeys.size === 0

  const totalToDelete = useMemo(() => {
    let count = 0
    for (const g of validityGroups) {
      if (selectedPeriodKeys.has(g.key)) {
        count += g.entries.length
      }
    }
    return count
  }, [validityGroups, selectedPeriodKeys])

  const handleConfirm = () => {
    const idsToDelete = new Set<string>()
    for (const g of validityGroups) {
      if (selectedPeriodKeys.has(g.key)) {
        for (const e of g.entries) {
          idsToDelete.add(e.id)
        }
      }
    }
    onConfirmDelete(idsToDelete)
  }

  const hasMultiplePeriods = validityGroups.length > 1

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Einträge löschen"
      size="md"
    >
      <div className="flex flex-col gap-4 p-1">
        {hasMultiplePeriods && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              Die Auswahl enthält Einträge aus <strong>{validityGroups.length} verschiedenen Gültigkeitszeiträumen</strong>.
              Wähle, welche Zeiträume gelöscht werden sollen.
            </div>
          </div>
        )}

        {!hasMultiplePeriods && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
            <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <strong>{selectedEntries.length} Einträge</strong> im Zeitraum «{validityGroups[0]?.label}» werden gelöscht.
            </div>
          </div>
        )}

        {hasMultiplePeriods && (
          <div className="border rounded-lg divide-y">
            <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Gültigkeitszeiträume</span>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  disabled={allSelected}
                  className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Alle auswählen
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={deselectAll}
                  disabled={noneSelected}
                  className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Keine auswählen
                </button>
              </div>
            </div>
            {validityGroups.map(group => {
              const isChecked = selectedPeriodKeys.has(group.key)
              const isCurrent = isPeriodCurrent(group.validFrom, group.validTo)

              return (
                <label
                  key={group.key}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isChecked ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => togglePeriod(group.key)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 flex items-center gap-2">
                    <span className={`text-sm font-medium ${isChecked ? 'text-gray-900' : 'text-gray-500'}`}>
                      {group.label}
                    </span>
                    {isCurrent && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        Aktuell
                      </span>
                    )}
                    {!isCurrent && group.validTo && new Date(group.validTo) < new Date() && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                        Abgelaufen
                      </span>
                    )}
                  </div>
                  <span className={`text-sm ${isChecked ? 'text-gray-700' : 'text-gray-400'}`}>
                    {group.entries.length} {group.entries.length === 1 ? 'Eintrag' : 'Einträge'}
                  </span>
                </label>
              )
            })}
          </div>
        )}

        {hasMultiplePeriods && !noneSelected && (
          <div className="text-sm text-gray-600">
            Es werden <strong>{totalToDelete} von {selectedEntries.length} Einträgen</strong> gelöscht.
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={deleting}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={noneSelected || deleting}
          >
            {deleting ? 'Löschen...' : `${totalToDelete} Einträge löschen`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
